import { formatDateSimple, getFieldValue } from '@supply-mover-2-0/core';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	FieldConfiguration,
	ListBuilderCallbacks,
	TableRow,
} from '../../types';
import { SIZE_MAP } from '../../utils/colorRuleEngine';
import { getFieldDisplayProperties } from './displayHelpers';

const DEFAULT_TIMEZONE = 'America/New_York';

interface DateDisplayProps {
	field: FieldConfiguration;
	value: unknown;
	data: TableRow;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

/**
 * Checks if a datetime string has a non-midnight time component
 */
function hasTimeComponent(dateValue: string | Date): boolean {
	if (!dateValue) {
		return false;
	}

	try {
		// If it's just a date string (YYYY-MM-DD), no time component
		if (
			typeof dateValue === 'string' &&
			/^\d{4}-\d{2}-\d{2}$/.test(dateValue)
		) {
			return false;
		}

		const date =
			typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

		if (isNaN(date.getTime())) {
			return false;
		}

		// Check if time is midnight (00:00:00) - treat as all-day/no time
		const hours = date.getUTCHours();
		const minutes = date.getUTCMinutes();
		const seconds = date.getUTCSeconds();

		return hours !== 0 || minutes !== 0 || seconds !== 0;
	} catch {
		return false;
	}
}

/**
 * Formats a datetime with timezone support
 * Converts from stored timezone to user's browser timezone
 */
function formatDateTimeWithTimezone(
	dateValue: string | Date,
	storedTimezone: string | null,
	format: 'short' | 'medium' | 'long' = 'medium',
): string {
	if (!dateValue) {
		return '-';
	}

	try {
		const tz = storedTimezone ?? DEFAULT_TIMEZONE;
		const date =
			typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

		if (isNaN(date.getTime())) {
			return '-';
		}

		// Format in user's browser timezone
		const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		const dateOptions: Intl.DateTimeFormatOptions = {
			timeZone: browserTimezone,
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		};

		switch (format) {
			case 'short':
				dateOptions.month = 'numeric';
				dateOptions.day = 'numeric';
				dateOptions.year = '2-digit';
				break;
			case 'medium':
				dateOptions.month = 'short';
				dateOptions.day = 'numeric';
				dateOptions.year = 'numeric';
				break;
			case 'long':
				dateOptions.month = 'long';
				dateOptions.day = 'numeric';
				dateOptions.year = 'numeric';
				break;
		}

		return new Intl.DateTimeFormat('en-US', dateOptions).format(date);
	} catch {
		return '-';
	}
}

function formatDateForDisplay(
	dateValue: string | Date,
	format: string,
	options?: {
		showTimeIfSet?: boolean;
		timezone?: string | null;
	},
): string {
	if (!dateValue) {
		return '-';
	}

	try {
		// If showTimeIfSet is enabled and there's a time component, show datetime
		if (options?.showTimeIfSet && hasTimeComponent(dateValue)) {
			// Determine the date format to use
			let dateFormat: 'short' | 'medium' | 'long' = 'medium';

			if (format === 'short' || format === 'date-short') {
				dateFormat = 'short';
			} else if (format === 'long' || format === 'date-long') {
				dateFormat = 'long';
			}

			return formatDateTimeWithTimezone(
				dateValue,
				options.timezone ?? null,
				dateFormat,
			);
		}

		// Normalize format by stripping "date-" prefix if present
		const normalizedFormat = format.startsWith('date-')
			? format.substring(5)
			: format;

		// Handle month-year format (MM/YYYY) for expiration dates
		if (normalizedFormat === 'month-year') {
			let date: Date;

			if (
				typeof dateValue === 'string' &&
				/^\d{4}-\d{2}-\d{2}$/.test(dateValue)
			) {
				// Handle YYYY-MM-DD format to avoid timezone shifts
				date = new Date(`${dateValue}T00:00:00`);
			} else {
				date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
			}

			if (isNaN(date.getTime())) {
				return '-';
			}

			const month = String(date.getMonth() + 1).padStart(2, '0');
			const year = date.getFullYear();

			return `${month}/${year}`;
		}

		// Handle simple date formats using core library function
		// Pass the raw value so formatDateSimple can handle UTC midnight timestamps correctly
		if (['short', 'medium', 'long', 'iso', 'mdy'].includes(normalizedFormat)) {
			return formatDateSimple(
				dateValue,
				normalizedFormat as 'short' | 'medium' | 'long' | 'iso' | 'mdy',
			);
		}

		// For datetime formats, we need to handle timezone properly for date-only strings
		let date: Date;

		if (
			typeof dateValue === 'string' &&
			/^\d{4}-\d{2}-\d{2}$/.test(dateValue)
		) {
			// Handle YYYY-MM-DD format to avoid timezone shifts
			date = new Date(`${dateValue}T00:00:00`);
		} else {
			date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
		}

		if (isNaN(date.getTime())) {
			return '-';
		}

		// Handle datetime formats
		switch (format) {
			case 'datetime-short': {
				// M/D/YY h:mm AM/PM
				const dateStr = formatDateSimple(date, 'short');
				const timeStr = date.toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: '2-digit',
					hour12: true,
				});

				return `${dateStr} ${timeStr}`;
			}

			case 'datetime-medium': {
				// Jun 13, 2025 2:30 PM
				const dateStr = formatDateSimple(date, 'medium');
				const timeStr = date.toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: '2-digit',
					hour12: true,
				});

				return `${dateStr} ${timeStr}`;
			}

			case 'datetime-long': {
				// June 13, 2025 at 2:30:45 PM
				const dateStr = formatDateSimple(date, 'long');
				const timeStr = date.toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: '2-digit',
					second: '2-digit',
					hour12: true,
				});

				return `${dateStr} at ${timeStr}`;
			}

			case 'datetime-iso': {
				// 2025-06-13T14:30:45
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				const hours = String(date.getHours()).padStart(2, '0');
				const minutes = String(date.getMinutes()).padStart(2, '0');
				const seconds = String(date.getSeconds()).padStart(2, '0');

				return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
			}

			default:
				return formatDateSimple(date, 'short');
		}
	} catch {
		return '-';
	}
}

/**
 * Formats date in "Tues 12/16/25" format with time on second line "10:00 AM EST"
 * Returns an object with dateLine and timeLine for multi-line rendering
 *
 * @param dateValue - The date/datetime value
 * @param storedTimezone - The timezone stored with the event (legacy check)
 * @param timeFieldValue - The value of the timeField (e.g., dueTime). If null, it's an all-day event.
 */
function formatWeekdayShortWithTime(
	dateValue: string | Date,
	storedTimezone: string | null,
	timeFieldValue?: string | null,
): { dateLine: string; timeLine: string | null } {
	if (!dateValue) {
		return { dateLine: '-', timeLine: null };
	}

	try {
		const date =
			typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

		if (isNaN(date.getTime())) {
			return { dateLine: '-', timeLine: null };
		}

		// Get user's browser timezone for display
		const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		// Format weekday (short) - e.g., "Tues"
		const weekday = date.toLocaleDateString('en-US', {
			weekday: 'short',
			timeZone: browserTimezone,
		});

		// Format date as M/D/YY - e.g., "12/16/25"
		const month = date.toLocaleDateString('en-US', {
			month: 'numeric',
			timeZone: browserTimezone,
		});
		const day = date.toLocaleDateString('en-US', {
			day: 'numeric',
			timeZone: browserTimezone,
		});
		const year = date.toLocaleDateString('en-US', {
			year: '2-digit',
			timeZone: browserTimezone,
		});

		const dateLine = `${weekday} ${month}/${day}/${year}`;

		// All-day detection: check timeFieldValue first (explicit indicator)
		// If timeFieldValue is null/undefined, it's an all-day event
		if (timeFieldValue == null) {
			return { dateLine, timeLine: 'All Day' };
		}

		// Fallback: check datetime for time component (legacy behavior)
		if (!hasTimeComponent(dateValue)) {
			// If timezone is null/undefined AND no time component, treat as all-day (legacy)
			if (storedTimezone == null) {
				return { dateLine, timeLine: 'All Day' };
			}

			return { dateLine, timeLine: null };
		}

		// Format time with timezone abbreviation - e.g., "10:00 AM EST"
		const timeStr = date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
			timeZone: browserTimezone,
			timeZoneName: 'short',
		});

		return { dateLine, timeLine: timeStr };
	} catch {
		return { dateLine: '-', timeLine: null };
	}
}

export function DateDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: DateDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig, link, dataConfig } = field;

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Note: For date display, we don't apply value mapping to the date itself
	// but we still evaluate color based on the original value
	const { colorValue } = getFieldDisplayProperties(field, value, data);
	const textColor =
		resolveThemeColor(colorValue?.text, isDark) ?? colors.values.textPrimary;

	if (!value && !dataConfig?.defaultValue) {
		return null;
	}

	// Support both legacy (top-level) and new (formatOptions) dateFormat location
	const dateFormat =
		styleConfig.formatOptions?.dateFormat ??
		((field as Record<string, unknown>).dateFormat as string | undefined) ??
		'short';
	const showTimeIfSet = styleConfig.formatOptions?.showTimeIfSet === true;
	const timezoneField = styleConfig.formatOptions?.timezoneField;
	const timeField = styleConfig.formatOptions?.timeField as string | undefined;

	// Get timezone from data if timezoneField is configured
	let timezone: string | null = null;

	if (timezoneField != null && data != null) {
		timezone = getFieldValue(data, timezoneField) as string | null;
	}

	// Get time field value (for all-day detection: null means all-day)
	let timeFieldValue: string | null | undefined;

	if (timeField != null && data != null) {
		timeFieldValue = getFieldValue(data, timeField) as string | null;
	}

	// Get size configuration - fallback to 'md' if size key is not found in SIZE_MAP
	const sizeConfig = SIZE_MAP[styleConfig?.size ?? 'md'] ?? SIZE_MAP.md;

	// Check if this field has linking enabled
	const isLinkEnabled = link?.enabled && (data?.id || data?._id);

	// Handle weekday-short-with-time format specially (two-line display)
	if (dateFormat === 'weekday-short-with-time' && value) {
		const { dateLine, timeLine } = formatWeekdayShortWithTime(
			value as string | Date,
			timezone,
			timeFieldValue,
		);

		if (isLinkEnabled) {
			const dateContent = (
				<>
					<div style={{ fontSize: sizeConfig.fontSize }}>{t(dateLine)}</div>
					{timeLine != null && (
						<div
							style={{
								fontSize: sizeConfig.fontSize,
								color: colors.values.textSecondary,
							}}
						>
							{t(timeLine)}
						</div>
					)}
				</>
			);

			if (!isCallback && linkUrl != null) {
				return (
					<a
						href={linkUrl}
						onClick={handleLinkClick}
						className="hover:underline transition-all no-underline"
						style={{ color: colors.values.primary }}
					>
						{dateContent}
					</a>
				);
			}

			return (
				<button
					onClick={handleLinkClick}
					className="hover:underline transition-all text-left"
					style={{ color: colors.values.primary }}
					type="button"
				>
					{dateContent}
				</button>
			);
		}

		return (
			<div>
				<div style={{ color: textColor, fontSize: sizeConfig.fontSize }}>
					{t(dateLine)}
				</div>
				{timeLine != null && (
					<div
						style={{
							fontSize: sizeConfig.fontSize,
							color: colors.values.textSecondary,
						}}
					>
						{t(timeLine)}
					</div>
				)}
			</div>
		);
	}

	const formattedDate = value
		? formatDateForDisplay(value as string | Date, dateFormat, {
				showTimeIfSet,
				timezone,
			})
		: dataConfig?.defaultValue || '-';

	// Apply white-space styling based on allowTextWrap property
	const whiteSpaceStyle =
		styleConfig.allowTextWrap === false ? 'nowrap' : 'normal';

	if (isLinkEnabled) {
		if (!isCallback && linkUrl != null) {
			return (
				<a
					href={linkUrl}
					onClick={handleLinkClick}
					className="hover:underline transition-all no-underline"
					style={{
						color: colors.values.primary,
						fontSize: sizeConfig.fontSize,
						whiteSpace: whiteSpaceStyle,
					}}
				>
					{t(formattedDate)}
				</a>
			);
		}

		return (
			<button
				onClick={handleLinkClick}
				className="hover:underline transition-all text-left"
				style={{
					color: colors.values.primary,
					fontSize: sizeConfig.fontSize,
					whiteSpace: whiteSpaceStyle,
				}}
				type="button"
			>
				{t(formattedDate)}
			</button>
		);
	}

	return (
		<span
			style={{
				color: textColor,
				fontSize: sizeConfig.fontSize,
				whiteSpace: whiteSpaceStyle,
			}}
		>
			{t(formattedDate)}
		</span>
	);
}
